import { createContext, useContext, useState, type ReactNode } from "react";
import { businessUnits, spendCategories, regions, timelines, teamMembers } from "../../data/mock";

export interface ProjectFormData {
  /* Step 1 */
  projectName:   string;
  businessUnit:  string;
  category:      string;
  region:        string;
  budget:        string;
  timeline:      string;
  /* Step 2 */
  taxonomy:      "UNSPSC" | "Custom" | "NAICS" | "";
  uploadedFile:  string;
  /* Step 3 */
  costWeight:            number;
  riskTolerance:         number;
  consolidationTarget:   number;
  esgThreshold:          number;
  automationLevel:       number;
  approvalWorkflow:      string;
  alertFrequency:        string;
  /* Step 4 */
  teamMemberIds: string[];
}

export interface SetupContextValue {
  form: ProjectFormData;
  setField: <K extends keyof ProjectFormData>(key: K, val: ProjectFormData[K]) => void;
  completion: number;
}

const defaultForm: ProjectFormData = {
  projectName: "Q4 Indirect Spend Review", businessUnit: businessUnits[0], category: spendCategories[0], region: regions[0], budget: "5,000,000", timeline: timelines[0],
  taxonomy: "", uploadedFile: "",
  costWeight: 75, riskTolerance: 40, consolidationTarget: 60, esgThreshold: 55, automationLevel: 80,
  approvalWorkflow: "Sequential Approval", alertFrequency: "Daily Digest",
  teamMemberIds: [teamMembers[0].id],
};

const SetupContext = createContext<SetupContextValue | null>(null);

export function SetupProvider({ children }: { children: ReactNode }) {
  const [form, setForm] = useState<ProjectFormData>(defaultForm);

  const setField = <K extends keyof ProjectFormData>(key: K, val: ProjectFormData[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  /* Compute overall completion percentage */
  const step1Fields = [form.projectName, form.businessUnit, form.category, form.region].filter(Boolean).length;
  const step2Done   = form.taxonomy !== "" ? 1 : 0;
  const step3Done   = 1; // always has defaults
  const step4Done   = form.teamMemberIds.length > 0 ? 1 : 0;
  const completion  = Math.round(((step1Fields / 4) * 25 + step2Done * 25 + step3Done * 25 + step4Done * 25));

  return (
    <SetupContext.Provider value={{ form, setField, completion }}>
      {children}
    </SetupContext.Provider>
  );
}

export const useSetup = () => {
  const ctx = useContext(SetupContext);
  if (!ctx) throw new Error("useSetup must be inside SetupProvider");
  return ctx;
};
